import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { CASE_STUDIES } from "@/lib/data";

export default function Work() {
  return (
    <div data-testid="work-page" className="bg-bone">
      <section className="pt-20 md:pt-28 pb-16 border-b border-border">
        <div className="tr-container grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-8">
            <div className="overline text-vermilion mb-8">Selected work</div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight">
              Product companies,
              <br />
              engineered by ThinkRight.
            </h1>
          </div>
          <div className="col-span-12 lg:col-span-4 lg:pl-10 lg:border-l border-border">
            <p className="text-lg text-ink/70 leading-relaxed">
              A small portfolio. Deliberately. Each engagement is a long arc with
              founders — from zero prototype to production platform.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="tr-container space-y-24 md:space-y-32">
          {CASE_STUDIES.map((cs, i) => (
            <Reveal key={cs.slug} delay={i}>
              <Link
                to={`/work/${cs.slug}`}
                data-testid={`work-card-${cs.slug}`}
                className="grid grid-cols-12 gap-8 md:gap-12 group"
              >
                <div className={`col-span-12 ${i % 2 === 0 ? "md:col-span-7" : "md:col-span-7 md:col-start-6 md:order-2"}`}>
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={cs.hero_image}
                      alt={cs.name}
                      className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]"
                    />
                  </div>
                </div>
                <div className={`col-span-12 md:col-span-5 ${i % 2 === 0 ? "" : "md:col-start-1 md:row-start-1 md:order-1"} flex flex-col justify-center`}>
                  <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.22em] text-ink/50 mb-4">
                    <span>{cs.sector}</span>
                    <span>{cs.year}</span>
                  </div>
                  <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
                    {cs.name}
                  </h2>
                  <p className="mt-5 text-lg text-ink/70 leading-relaxed">
                    {cs.tagline}
                  </p>
                  <div className="mt-8 grid grid-cols-2 gap-0 border-t border-border max-w-md">
                    {cs.metrics.slice(0, 2).map((m, j) => (
                      <div key={m.label} className={`py-5 ${j === 0 ? "pr-5 border-r border-border" : "pl-5"}`}>
                        <div className="display-num text-3xl md:text-4xl">{m.value}</div>
                        <div className="mt-2 text-xs text-ink/60">{m.label}</div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-10 inline-flex items-center gap-2 text-sm group-hover:text-vermilion transition-colors">
                    Read full story <ArrowUpRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}

          {/* Coming soon placeholder */}
          <Reveal delay={CASE_STUDIES.length}>
            <div
              data-testid="work-card-coming-soon"
              className="grid grid-cols-12 gap-8 md:gap-12"
            >
              <div className="col-span-12 md:col-span-7 md:col-start-6 md:order-2">
                <div className="aspect-[4/3] overflow-hidden border border-dashed border-border bg-bone/40 flex items-center justify-center">
                  <span className="display-num text-5xl md:text-6xl text-ink/15">
                    Soon
                  </span>
                </div>
              </div>
              <div className="col-span-12 md:col-span-5 md:col-start-1 md:row-start-1 md:order-1 flex flex-col justify-center">
                <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.22em] text-ink/50 mb-4">
                  <span>New engagement</span>
                  <span>2026</span>
                </div>
                <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-ink/40">
                  More coming soon
                </h2>
                <p className="mt-5 text-lg text-ink/60 leading-relaxed">
                  New partner products are in the works. The next case study is
                  being written as we ship.
                </p>
                <Link
                  to="/contact"
                  data-testid="work-coming-soon-cta"
                  className="mt-10 inline-flex items-center gap-2 text-sm hover:text-vermilion transition-colors"
                >
                  Talk to us about a partnership <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Teaser for more */}
      <section className="py-24 md:py-32 border-t border-border">
        <div className="tr-container grid grid-cols-12 gap-10 items-end">
          <div className="col-span-12 md:col-span-8">
            <div className="overline text-vermilion mb-6">More, under NDA</div>
            <h3 className="font-serif text-3xl md:text-5xl leading-[1.05] tracking-tight">
              A fintech risk-decisioning platform.
              <br />
              A healthcare voice-AI copilot.
              <br />
              <span className="italic text-ink/60">Quietly shipping in production.</span>
            </h3>
          </div>
          <div className="col-span-12 md:col-span-4">
            <Link
              to="/contact"
              data-testid="work-cta-contact"
              className="btn-primary"
            >
              Request a private walkthrough <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
